import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe2, KeyRound, Plus, Save, Server, Trash2 } from "lucide-react";
import {
  adminPublicCollectProxyPoolSchema,
  type AdminPublicCollectProxyEntryView,
  type AdminPublicCollectProxyPoolInput,
  type PublicCollectProxyProtocol
} from "@shudian/contracts";
import {
  adminGetPublicCollectProxyPool,
  adminSavePublicCollectProxyPool
} from "../api/admin.js";
import { Alert } from "../components/ui/Alert.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";

interface ProxyFormEntry {
  id?: string;
  enabled: boolean;
  name: string;
  protocol: PublicCollectProxyProtocol;
  host: string;
  port: string;
  username: string;
  password: string;
  passwordConfigured: boolean;
  clearPassword: boolean;
}

const PROTOCOLS: PublicCollectProxyProtocol[] = [
  "http",
  "https",
  "socks4",
  "socks4a",
  "socks5",
  "socks5h"
];

export function PublicCollectProxyPoolPage() {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<ProxyFormEntry[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "public-collect-proxy-pool"],
    queryFn: () => adminGetPublicCollectProxyPool()
  });

  useEffect(() => {
    if (!data?.pool) return;
    setEntries(data.pool.proxies.map(proxyViewToForm));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: AdminPublicCollectProxyPoolInput) => adminSavePublicCollectProxyPool(input),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "public-collect-proxy-pool"] });
      setEntries(response.pool.proxies.map(proxyViewToForm));
      setSuccessMessage("公开采集代理池已保存");
    },
    onError: (err) => {
      setErrorMessage(err instanceof CloudApiError ? describeError(err.code) : "保存公开采集代理池失败");
    }
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载公开采集代理池失败";
    return <Alert tone="danger">{message}</Alert>;
  }

  const pool = data?.pool;
  const enabledCount = entries.filter((entry) => entry.enabled).length;
  const passwordCount = entries.filter((entry) => entry.passwordConfigured || entry.password.trim()).length;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    const input = buildInput(entries);
    const parsed = adminPublicCollectProxyPoolSchema.safeParse(input);
    if (!parsed.success) {
      setErrorMessage(parsed.error.issues.map((issue) => issue.message).join("；"));
      return;
    }
    mutation.mutate(parsed.data);
  };

  return (
    <div>
      <PageHeader
        eyebrow="发布与采集 / 采集代理池"
        title="采集代理池"
        description="仅用于云端公开采集露天商品页和关键词搜索；不会下发给桌面端，也不会用于店铺登录、卖家后台或自动上架。"
        actions={
          <Button type="button" variant="secondary" onClick={addEntry}>
            <Plus className="h-4 w-4" />
            添加代理
          </Button>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <MetricCard label="代理数量" value={`${entries.length} 个`} detail="云端公开采集出口" icon={<Server className="h-4 w-4" />} />
        <MetricCard label="启用出口" value={`${enabledCount} 个`} detail={enabledCount > 0 ? "采集会优先使用" : "当前会回退直连"} tone={enabledCount > 0 ? "success" : "warning"} icon={<Globe2 className="h-4 w-4" />} />
        <MetricCard label="密码状态" value={`${passwordCount} 个`} detail="已保存密码的代理" tone={passwordCount > 0 ? "success" : "neutral"} icon={<KeyRound className="h-4 w-4" />} />
        <MetricCard label="更新时间" value={formatDateTime(pool?.updatedAt ?? undefined)} detail="最后保存代理池" tone="neutral" />
      </section>

      <form className="space-y-6" onSubmit={submit} noValidate>
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
        {successMessage && <Alert tone="success">{successMessage}</Alert>}

        <SectionSurface>
          <SectionHeader
          title="代理出口"
          description="密码不会回显；编辑时新密码留空表示保留现有密码。"
          actions={
            <Button type="button" size="sm" variant="secondary" onClick={addEntry}>
              <Plus className="h-3.5 w-3.5" />
              添加代理
            </Button>
          }
        />
          <div className="px-4 py-4">
            {entries.length === 0 ? (
            <div className="rounded border border-dashed border-surface-border bg-surface-raised px-4 py-6 text-sm text-text-secondary">
              还没有配置云端公开采集代理。添加至少一个启用代理后，Cloud API 的露天公开采集会按顺序轮询这些出口。
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={entry.id ?? `new-${index}`} className="rounded border border-surface-border bg-surface-raised p-4">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-text-primary">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={entry.enabled}
                          onChange={(event) => updateEntry(index, { enabled: event.target.checked })}
                        />
                        启用
                      </label>
                      <Badge tone={entry.passwordConfigured ? "success" : "neutral"}>
                        {entry.passwordConfigured ? "密码已保存" : "未保存密码"}
                      </Badge>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeEntry(index)}>
                      <Trash2 className="h-3.5 w-3.5" />
                      删除
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                    <Field label="名称" htmlFor={`proxy-${index}-name`} required className="md:col-span-2">
                      <Input
                        id={`proxy-${index}-name`}
                        value={entry.name}
                        onChange={(event) => updateEntry(index, { name: event.target.value })}
                      />
                    </Field>
                    <Field label="协议" htmlFor={`proxy-${index}-protocol`} required>
                      <select
                        id={`proxy-${index}-protocol`}
                        className="h-9 w-full rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40"
                        value={entry.protocol}
                        onChange={(event) =>
                          updateEntry(index, { protocol: event.target.value as PublicCollectProxyProtocol })
                        }
                      >
                        {PROTOCOLS.map((protocol) => (
                          <option key={protocol} value={protocol}>
                            {protocol}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="主机" htmlFor={`proxy-${index}-host`} required className="md:col-span-2">
                      <Input
                        id={`proxy-${index}-host`}
                        value={entry.host}
                        placeholder="proxy.example.com"
                        onChange={(event) => updateEntry(index, { host: event.target.value })}
                      />
                    </Field>
                    <Field label="端口" htmlFor={`proxy-${index}-port`} required>
                      <Input
                        id={`proxy-${index}-port`}
                        type="number"
                        min={1}
                        max={65535}
                        step={1}
                        value={entry.port}
                        onChange={(event) => updateEntry(index, { port: event.target.value })}
                      />
                    </Field>
                    <Field label="用户名" htmlFor={`proxy-${index}-username`} className="md:col-span-2">
                      <Input
                        id={`proxy-${index}-username`}
                        value={entry.username}
                        autoComplete="off"
                        onChange={(event) => updateEntry(index, { username: event.target.value })}
                      />
                    </Field>
                    <Field
                      label="新密码"
                      htmlFor={`proxy-${index}-password`}
                      hint={entry.passwordConfigured ? "留空保留现有密码" : "无密码代理可留空"}
                      className="md:col-span-2"
                    >
                      <Input
                        id={`proxy-${index}-password`}
                        type="password"
                        autoComplete="off"
                        value={entry.password}
                        disabled={entry.clearPassword}
                        onChange={(event) => updateEntry(index, { password: event.target.value })}
                      />
                    </Field>
                    <Field label="清空密码" htmlFor={`proxy-${index}-clearPassword`} className="md:col-span-2">
                      <label className="flex h-9 items-center gap-2 text-sm text-text-primary">
                        <input
                          id={`proxy-${index}-clearPassword`}
                          type="checkbox"
                          className="h-4 w-4"
                          checked={entry.clearPassword}
                          disabled={!entry.passwordConfigured}
                          onChange={(event) =>
                            updateEntry(index, {
                              clearPassword: event.target.checked,
                              password: event.target.checked ? "" : entry.password
                            })
                          }
                        />
                        保存时清除已保存密码
                      </label>
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </SectionSurface>

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            <Save className="h-3.5 w-3.5" />
            {mutation.isPending ? "保存中..." : "保存代理池"}
          </Button>
        </div>
      </form>
    </div>
  );

  function addEntry() {
    setEntries((current) => [
      ...current,
      {
        enabled: true,
        name: `代理 ${current.length + 1}`,
        protocol: "socks5h",
        host: "",
        port: "1080",
        username: "",
        password: "",
        passwordConfigured: false,
        clearPassword: false
      }
    ]);
  }

  function updateEntry(index: number, patch: Partial<ProxyFormEntry>) {
    setEntries((current) =>
      current.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...patch } : entry))
    );
  }

  function removeEntry(index: number) {
    setEntries((current) => current.filter((_, entryIndex) => entryIndex !== index));
  }
}

function proxyViewToForm(proxy: AdminPublicCollectProxyEntryView): ProxyFormEntry {
  return {
    id: proxy.id,
    enabled: proxy.enabled,
    name: proxy.name,
    protocol: proxy.protocol,
    host: proxy.host,
    port: String(proxy.port),
    username: proxy.username ?? "",
    password: "",
    passwordConfigured: proxy.passwordConfigured,
    clearPassword: false
  };
}

function buildInput(entries: ProxyFormEntry[]): AdminPublicCollectProxyPoolInput {
  return {
    proxies: entries.map((entry) => ({
      id: entry.id,
      enabled: entry.enabled,
      name: entry.name.trim(),
      protocol: entry.protocol,
      host: entry.host.trim(),
      port: parseIntegerInput(entry.port),
      username: optionalValue(entry.username),
      password: entry.clearPassword ? undefined : optionalValue(entry.password),
      clearPassword: entry.clearPassword
    }))
  };
}

function optionalValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function parseIntegerInput(value: string): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : -1;
}
